/**
 * NotificationCenter — bell icon with unread badge and
 * dropdown panel listing recent notifications.
 *
 * Real-time via useNotifications hook. Supports mark read,
 * mark all read, and archive. Includes mobile support.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Bell, Check, Archive, X, CheckCheck } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/hooks/auth';
import { useNotifications } from '@/hooks/useNotifications';
import { analyticsService } from '@/lib/AnalyticsService';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useScrollLock } from '@/hooks/useScrollLock';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { NotificationRow } from '@/lib/notification/types';

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function notificationIcon(type: NotificationRow['type']): string {
  switch (type) {
    case 'application_status_change':
      return '📋';
    case 'review_update':
      return '✅';
    case 'profile_update':
      return '👤';
    case 'announcement':
      return '📢';
    case 'system':
      return '⚙️';
    default:
      return '🔔';
  }
}

export function NotificationCenter(): JSX.Element | null {
  const { user } = useAuth();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);

  const { notifications, unreadCount, isLoading, markRead, markAllRead, archive } =
    useNotifications(user?.id);

  useFocusTrap(panelRef, isOpen && !isDesktop);
  useScrollLock(isOpen && !isDesktop);

  useEffect(() => {
    if (!isOpen || !isDesktop) return;
    const handler = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        bellRef.current &&
        !bellRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, isDesktop]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  const handleNotificationClick = useCallback(
    (notif: NotificationRow) => {
      analyticsService.track('notification_opened', { notificationId: notif.id, type: notif.type });
      if (!notif.read_at) {
        analyticsService.track('notification_marked_read', { notificationId: notif.id });
        void markRead(notif.id);
      }
      if (notif.action_url) {
        window.location.href = notif.action_url;
      }
    },
    [markRead],
  );

  if (!user) return null;

  const badge = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <>
      <button
        ref={bellRef}
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        className={cn(
          'relative flex h-10 w-10 items-center justify-center rounded-md text-neutral-600',
          'transition-colors hover:bg-neutral-100 hover:text-neutral-900',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/30',
        )}
      >
        <Bell size={20} className={cn(unreadCount > 0 && 'text-primary-700')} />
        {unreadCount > 0 && (
          <span
            className={cn(
              'absolute -right-0.5 -top-0.5 flex min-w-[18px] h-[18px] items-center justify-center',
              'rounded-full bg-error-600 px-1 text-[10px] font-bold text-white',
              'animate-scale-in',
            )}
            aria-label={`${badge} unread notifications`}
          >
            {badge}
          </span>
        )}
      </button>

      {isOpen && isDesktop && (
        <div
          ref={panelRef}
          role="menu"
          aria-label="Notifications"
          className="absolute right-0 top-12 z-50 w-96 max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-surface shadow-lg"
        >
          <NotificationPanel
            notifications={notifications}
            unreadCount={unreadCount}
            isLoading={isLoading}
            onMarkAllRead={markAllRead}
            onNotificationClick={handleNotificationClick}
            onArchive={archive}
            onClose={() => setIsOpen(false)}
          />
        </div>
      )}

      {isOpen && !isDesktop && (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Notifications">
          <div className="absolute inset-0 bg-overlay" onClick={() => setIsOpen(false)} />
          <div
            ref={panelRef}
            className="absolute inset-y-0 right-0 flex w-full max-w-sm flex-col bg-surface shadow-xl"
          >
            <NotificationPanel
              notifications={notifications}
              unreadCount={unreadCount}
              isLoading={isLoading}
              onMarkAllRead={markAllRead}
              onNotificationClick={handleNotificationClick}
              onArchive={archive}
              onClose={() => setIsOpen(false)}
              isMobile
            />
          </div>
        </div>
      )}
    </>
  );
}

interface NotificationPanelProps {
  notifications: NotificationRow[];
  unreadCount: number;
  isLoading: boolean;
  onMarkAllRead: () => void;
  onNotificationClick: (notif: NotificationRow) => void;
  onArchive: (id: string) => void;
  onClose: () => void;
  isMobile?: boolean;
}

function NotificationPanel({
  notifications,
  unreadCount,
  isLoading,
  onMarkAllRead,
  onNotificationClick,
  onArchive,
  onClose,
  isMobile,
}: NotificationPanelProps): JSX.Element {
  return (
    <div className="flex max-h-[28rem] flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-foreground">Notifications</h2>
          {unreadCount > 0 && (
            <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-800">
              {unreadCount} new
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={onMarkAllRead}
              className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-50"
              aria-label="Mark all as read"
            >
              <CheckCheck size={14} />
              Mark all read
            </button>
          )}
          {isMobile && (
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-md text-neutral-400 hover:bg-neutral-100"
              aria-label="Close notifications"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-sm text-muted-foreground">Loading...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-12">
            <Bell size={24} className="text-neutral-300" />
            <p className="text-sm text-muted-foreground">No notifications yet</p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {notifications.map((notif) => (
              <li
                key={notif.id}
                className={cn(
                  'group relative flex gap-3 px-4 py-3 transition-colors hover:bg-surface-secondary',
                  !notif.read_at && 'bg-primary-50/40',
                )}
              >
                <button
                  type="button"
                  onClick={() => onNotificationClick(notif)}
                  className="flex flex-1 items-start gap-3 text-left"
                >
                  <span className="mt-0.5 text-base" aria-hidden="true">
                    {notificationIcon(notif.type)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p
                        className={cn(
                          'text-sm truncate',
                          !notif.read_at ? 'font-semibold text-foreground' : 'font-medium text-neutral-700',
                        )}
                      >
                        {notif.title}
                      </p>
                      {!notif.read_at && (
                        <span className="h-2 w-2 shrink-0 rounded-full bg-primary-600" aria-label="Unread" />
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{notif.body}</p>
                    <p className="mt-1 text-[11px] text-neutral-400">{formatRelative(notif.created_at)}</p>
                  </div>
                </button>

                {/* Hover actions */}
                <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                  {!notif.read_at && (
                    <button
                      type="button"
                      onClick={() => onNotificationClick(notif)}
                      className="flex h-7 w-7 items-center justify-center rounded-md text-neutral-400 hover:bg-neutral-200 hover:text-neutral-700"
                      aria-label="Mark as read"
                    >
                      <Check size={14} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onArchive(notif.id)}
                    className="flex h-7 w-7 items-center justify-center rounded-md text-neutral-400 hover:bg-neutral-200 hover:text-neutral-700"
                    aria-label="Archive notification"
                  >
                    <Archive size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
